import { useCallback, useEffect } from 'react';
import useCssVars from '@/hooks/useCssVars';
import { useInstitutionsStore } from '@/stores/institutions';
import { DEFAULT_COLORS } from '@/utils/constants';
import { generateColorShades } from '@/utils/generateColorShades';

/**
 * Custom hook to apply branding colors to CSS variables based on the current institution's whitelabel settings
 * or explicit primary/text color inputs.
 * @param primary - Optional primary color override.
 * @param text - Optional button text color override.
 * @returns An object containing `setBrandingColors` function to manually apply colors.
 */
export const useBrandingColors = (primary?: string, text?: string) => {
  const { setCssVar } = useCssVars();
  const { currentInstitution } = useInstitutionsStore((state) => ({
    currentInstitution: state.currentInstitution,
  }));

  const applyColors = useCallback(
    (colors: Record<string, string>) => {
      Object.entries(colors).forEach(([key, value]) => {
        setCssVar(key, value);
      });
    },
    [setCssVar],
  );

  const resetBrandingColors = useCallback(() => {
    applyColors({
      'colors-primary100': DEFAULT_COLORS.primary100,
      'colors-primary200': DEFAULT_COLORS.primary200,
      'colors-primary300': DEFAULT_COLORS.primary300,
      'colors-primary500': DEFAULT_COLORS.primary500,
      'colors-primary600': DEFAULT_COLORS.primary600,
      'colors-primary700': DEFAULT_COLORS.primary700,
      'colors-accent': DEFAULT_COLORS.primary700,
      'colors-button-text': DEFAULT_COLORS.buttonText,
    });
  }, [applyColors]);

  const setBrandingColors = useCallback(
    (
      primaryColor: string = DEFAULT_COLORS.primary600,
      buttonText: string = DEFAULT_COLORS.buttonText,
    ) => {
      if (!primaryColor && !buttonText) {
        resetBrandingColors();
        return;
      }
      const colors: Record<string, string> = {};
      if (primaryColor) {
        const shades = generateColorShades(primaryColor);
        Object.assign(colors, {
          'colors-primary100': shades.primary100,
          'colors-primary200': shades.primary200,
          'colors-primary300': shades.primary300,
          'colors-primary500': shades.primary500,
          'colors-primary600': primaryColor,
          'colors-primary700': shades.primary700,
          'colors-accent': shades.primary700,
        });
      }
      if (buttonText) {
        colors['colors-button-text'] = buttonText;
      }
      applyColors(colors);
    },
    [applyColors, resetBrandingColors],
  );

  useEffect(() => {
    if (!currentInstitution?.whitelabel?.enabled) return;
    if (
      currentInstitution?.whitelabel?.primaryColor ||
      currentInstitution?.whitelabel?.buttonTextColor
    ) {
      setBrandingColors(
        currentInstitution.whitelabel.primaryColor,
        currentInstitution.whitelabel.buttonTextColor,
      );
    } else {
      resetBrandingColors();
    }
  }, [currentInstitution, setBrandingColors, resetBrandingColors]);

  useEffect(() => {
    if (currentInstitution) return;
    if (primary || text) {
      setBrandingColors(primary, text);
    } else {
      resetBrandingColors();
    }
  }, [
    setBrandingColors,
    resetBrandingColors,
    primary,
    text,
    currentInstitution,
  ]);

  return { setBrandingColors };
};
